<template>
  <div class="containerTag" :class="{ 'with-count': showCount }">

    <datalist v-if="options" :id="id">
      <option v-for="option in availableOptions" :key="option" :value="option">
        {{ option }}
      </option>
    </datalist>

    <ul class="tags" ref="tagsUl">
      <li v-for="(tag, index) in modelValue" :key="tag" class="tag" :class="{ duplicate: tag === duplicate }">
        {{ tag }}
        <button class="delete" @click="removeTag(index)">x</button>
      </li>
      <input v-model="newTag" :placeholder="placeholder" type="text" :list="id" autocomplete="off" @keydown.enter="addTag(newTag)" @keydown.prevent.tab="addTag(newTag)" @keydown.delete="newTag.length || removeTag(tags.length - 1)" />
    </ul>

    <div v-if="showCount" class="count">
      <span>{{ modelValue.length }}</span> itens
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, computed, defineExpose, defineProps, defineEmits } from 'vue';

const props = defineProps({
  name: { type: String, default: '' },
  modelValue: { type: Array, default: () => [] },
  options: { type: [Array, Boolean], default: false },
  allowCustom: { type: Boolean, default: true },
  showCount: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Digite aqui' },
});

// Tags é onde ele guarda os valores internamente,
// ele vai replicar isso no modelValue que é a propriedade que será exposta no componente.
const tags = ref(props.modelValue);
const newTag = ref('');
const id = Math.random().toString(36).substring(7);

// handling duplicates
const duplicate = ref(null);
const handleDuplicate = (tag) => {
  duplicate.value = tag;
  setTimeout(() => { duplicate.value = null; }, 1000);
  newTag.value = '';
};

const addTag = (tag) => {
  if (!tag) return; // prevent empty tag
  // only allow predefined tags when allowCustom is false
  if (!props.allowCustom && !props.options.includes(tag)) return;
  // return early if duplicate
  if (tags.value.includes(tag)) {
    handleDuplicate(tag);
    return;
  }
  tags.value.push(tag);
  newTag.value = ''; // reset newTag
};
const removeTag = (index) => {
  tags.value.splice(index, 1);
};

const emits = defineEmits(['update', 'update:modelValue']);

// positioning and handling tag change
const tagsUl = ref(null);
function onTagsChange() {
  // scroll to end of tags
  tagsUl.value.scrollTo(tagsUl.value.scrollWidth, 0);
  // Vamos enviar uma requisição para a view poder alterar o valor do componente.
  // Quando temos alteração no tags, precisamos de replicar isso na variável que está sendo bindada no modelValue.
  emits('update:modelValue', tags.value);
}

// Essa lista de opções pode ser definida para obrigar o usuário a selecionar só um registro que esteja na lista.
const availableOptions = computed(() => {
  if (!props.options) return false;
  return props.options.filter((option) => !tags.value.includes(option));
});

watch(() => tags, () => { nextTick(onTagsChange); }, { deep: true });
onMounted(onTagsChange);
watch(() => props.modelValue, () => { tags.value = props.modelValue; }, { deep: true });

defineExpose({ props,
  tags,
  newTag,
  addTag,
  removeTag,
  tagsUl,
  availableOptions,
  id,
  duplicate });
</script>

<style scoped>
.containerTag {
  position: relative;
  width: 100%;
  height: auto;
  border: 1px solid #D8D6DE;
  display: flex;
  flex-flow: row wrap;
  min-height: 40px;
  border-radius: 5px;
  padding: .571rem 1rem;
  margin-top: 10px;
}

.containerTag input {
  flex: 3;
  border: 0;
  outline: 0;
}

.containerTag input:focus {
  border: none;
  border-radius: 3px;
  outline: none;
}

.containerTag:hover{
  border-color: #0069d9;
  outline: 0 none;
  background: white;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.045), 0 0 1px #AAA;
}

ul {
  list-style: none;
  display: block;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 10px;
  max-width: 95%;
}

input {
  width: 100%;
  color: #1d1d1d;
  min-width: 95px;
}

.delete {
  color: white;
  background: none;
  outline: none;
  border: none;
  cursor: pointer;
}

@keyframes shake {

  10%,
  90% {
    transform: scale(0.9) translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: scale(0.9) translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: scale(0.9) translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: scale(0.9) translate3d(4px, 0, 0);
  }
}

.tag.duplicate {
  background: rgb(0, 27, 27);
  animation: shake 1s;
}

.count {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  display: block;
  font-size: 0.8rem;
  white-space: nowrap;
}

.count span {
  background: #eee;
  padding: 2px;
  border-radius: 2px;
}

.tags {
  box-sizing: border-box;
  list-style: none;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: auto;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
}

.containerTag:after {
  content: ".";
  display: block;
  clear: both;
  visibility: hidden;
}

.tag {
  background-color: #0069d9 !important;
  border-color: #0069d9 !important;
  color: #fff;
  padding: 0.2px 5px;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: default;
  float: left;
  margin-right: 1px;
  font-size: 0.9rem;
}

.dark-layout input{
  background: #283046;
  color: white;
}

.dark-layout .containerTag:hover{
  background: #283046;
}

</style>
